import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Phone,
  Email,
  Schedule,
  Warning,
  CheckCircle,
  TrendingUp,
  Assignment,
  CalendarToday,
} from '@mui/icons-material';
import { leadsService } from '../../services/leads';
import { LEAD_STATUS_LABELS, LEAD_PRIORITY_LABELS } from '../../services/leads';

const BDADashboard = () => {
  const { user } = useSelector((state) => state.auth);
  
  const [myLeads, setMyLeads] = useState([]);
  const [followUpLeads, setFollowUpLeads] = useState([]);
  const [myPerformance, setMyPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    if (user?.id) {
      fetchMyLeads();
      fetchFollowUpLeads();
      fetchMyPerformance();
    }
  }, [user]);

  const fetchMyLeads = async () => {
    try {
      const response = await leadsService.getLeads({
        assigned_to: user.id,
        ordering: '-created_at',
        page_size: 50
      });
      setMyLeads(response.results || response);
    } catch (error) {
      console.error('Failed to fetch my leads:', error);
    }
  };

  const fetchFollowUpLeads = async () => {
    try {
      const response = await leadsService.getFollowUpLeads();
      setFollowUpLeads(response.leads || response);
    } catch (error) {
      console.error('Failed to fetch follow-up leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPerformance = async () => {
    try {
      const performance = await leadsService.getUserPerformance();
      setMyPerformance(performance);
    } catch (error) {
      console.error('Failed to fetch my performance:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'primary',
      'warm': 'info',
      'hot': 'warning',
      'followup': 'warning',
      'ipd_done': 'success',
      'opd_done': 'success',
      'converted': 'success',
      'dnp': 'error',
      'dead': 'error'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success'
    };
    return colors[priority] || 'default';
  };

  const isFollowupOverdue = (followupDate) => {
    if (!followupDate) return false;
    const today = new Date();
    const followup = new Date(followupDate);
    return followup < today;
  };

  const isFollowupToday = (followupDate) => {
    if (!followupDate) return false;
    const today = new Date();
    const followup = new Date(followupDate);
    return followup.toDateString() === today.toDateString();
  };

  const isFollowupTomorrow = (followupDate) => {
    if (!followupDate) return false;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const followup = new Date(followupDate);
    return followup.toDateString() === tomorrow.toDateString();
  };

  const getDaysOverdue = (followupDate) => {
    if (!followupDate) return 0;
    const today = new Date();
    const followup = new Date(followupDate);
    const diffTime = today - followup;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const renderPerformanceCards = () => {
    if (!myPerformance) return null;

    const { leads, calls, status_breakdown } = myPerformance;
    const conversionRate = status_breakdown
      ? ((status_breakdown
          .filter(s => s.status === 'ipd_done' || s.status === 'opd_done')
          .reduce((sum, s) => sum + s.count, 0) /
        status_breakdown.reduce((sum, s) => sum + s.count, 0)) * 100).toFixed(1)
      : 0;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{leads?.total || myLeads.length}</Typography>
                  <Typography color="text.secondary">My Leads</Typography>
                  <Typography variant="caption" color="primary">
                    {leads?.this_month || 0} this month
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Phone color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{calls?.total || 0}</Typography>
                  <Typography color="text.secondary">Calls Made</Typography>
                  <Typography variant="caption" color="success">
                    {calls?.this_week || 0} this week
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">{conversionRate}%</Typography>
                  <Typography color="text.secondary">Conversion Rate</Typography>
                  <Typography variant="caption" color="warning">
                    IPD + OPD Done
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4">
                    {followUpLeads.filter(lead => isFollowupOverdue(lead.follow_up_date)).length}
                  </Typography>
                  <Typography color="text.secondary">Overdue Follow-ups</Typography>
                  <Typography variant="caption" color="error">
                    Need attention
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderMyLeads = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          My Assigned Leads ({myLeads.length})
        </Typography>
        
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lead Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Disease</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Follow-up</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {lead.name?.charAt(0)}
                      </Avatar>
                      <Typography variant="body2" fontWeight="bold">
                        {lead.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lead.phone}</Typography>
                    {lead.email && (
                      <Typography variant="caption" color="text.secondary">
                        {lead.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{lead.city_name}</TableCell>
                  <TableCell>{lead.disease_name}</TableCell>
                  <TableCell>
                    <Chip
                      label={LEAD_STATUS_LABELS[lead.status]}
                      color={getStatusColor(lead.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={LEAD_PRIORITY_LABELS[lead.priority]}
                      color={getPriorityColor(lead.priority)}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {lead.follow_up_date ? (
                      <Box>
                        <Typography variant="body2">
                          {new Date(lead.follow_up_date).toLocaleDateString()}
                        </Typography>
                        {isFollowupOverdue(lead.follow_up_date) && (
                          <Chip 
                            label={`${getDaysOverdue(lead.follow_up_date)} days overdue`}
                            color="error" 
                            size="small"
                            icon={<Warning />}
                          />
                        )}
                        {isFollowupToday(lead.follow_up_date) && (
                          <Chip 
                            label="Today" 
                            color="warning" 
                            size="small"
                            icon={<CalendarToday />}
                          />
                        )}
                        {isFollowupTomorrow(lead.follow_up_date) && (
                          <Chip 
                            label="Tomorrow" 
                            color="info" 
                            size="small"
                            icon={<CalendarToday />}
                          />
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not set
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Call Lead">
                      <IconButton
                        size="small"
                        color="primary"
                        href={`tel:${lead.phone}`}
                      >
                        <Phone />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="WhatsApp">
                      <IconButton
                        size="small"
                        color="success"
                        href={`https://wa.me/91${lead.phone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Phone />
                      </IconButton>
                    </Tooltip>
                    {lead.email && (
                      <Tooltip title="Send Email">
                        <IconButton
                          size="small"
                          color="info"
                          href={`mailto:${lead.email}`}
                        >
                          <Email />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {myLeads.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No leads assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your assigned leads will appear here
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderFollowUps = () => {
    const todayFollowUps = followUpLeads.filter(lead => isFollowupToday(lead.follow_up_date));
    const overdueFollowUps = followUpLeads.filter(lead => isFollowupOverdue(lead.follow_up_date));
    const upcomingFollowUps = followUpLeads.filter(lead => 
      !isFollowupOverdue(lead.follow_up_date) && !isFollowupToday(lead.follow_up_date)
    );

    return (
      <Grid container spacing={3}>
        {/* Overdue Follow-ups */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="error" gutterBottom>
                <Warning sx={{ mr: 1, verticalAlign: 'middle' }} />
                Overdue ({overdueFollowUps.length})
              </Typography>
              
              {overdueFollowUps.map((lead) => (
                <Card key={lead.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {lead.name}
                      </Typography>
                      <Chip 
                        label={`${getDaysOverdue(lead.follow_up_date)} days`}
                        color="error" 
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {lead.phone} • {lead.city_name}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button size="small" startIcon={<Phone />} href={`tel:${lead.phone}`}>
                        Call
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {overdueFollowUps.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CheckCircle color="success" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No overdue follow-ups!
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Follow-ups */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main" gutterBottom>
                <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                Today ({todayFollowUps.length})
              </Typography>
              
              {todayFollowUps.map((lead) => (
                <Card key={lead.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {lead.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.phone} • {lead.city_name}
                    </Typography>
                    <Typography variant="caption" display="block" color="warning.main">
                      {new Date(lead.follow_up_date).toLocaleTimeString()}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                      <Button size="small" startIcon={<Phone />} href={`tel:${lead.phone}`}>
                        Call
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}

              {todayFollowUps.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CalendarToday color="info" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No follow-ups for today
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Upcoming Follow-ups */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main" gutterBottom>
                <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                Upcoming ({upcomingFollowUps.length})
              </Typography>
              
              {upcomingFollowUps.slice(0, 5).map((lead) => (
                <Card key={lead.id} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {lead.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lead.phone} • {lead.city_name}
                    </Typography>
                    <Typography variant="caption" display="block" color="info.main">
                      {new Date(lead.follow_up_date).toLocaleDateString()}
                    </Typography>
                  </CardContent>
                </Card>
              ))}

              {upcomingFollowUps.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Schedule color="info" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No upcoming follow-ups
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ textAlign: 'center', mt: 2 }}>
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        My Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome back, {user?.first_name}! Here's your performance overview and tasks.
      </Typography>

      {/* Performance Cards */}
      {renderPerformanceCards()}

      {/* Urgent Alerts */}
      {followUpLeads.filter(lead => isFollowupOverdue(lead.follow_up_date)).length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2">
            You have <strong>{followUpLeads.filter(lead => isFollowupOverdue(lead.follow_up_date)).length}</strong> overdue follow-ups 
            that need immediate attention!
          </Typography>
        </Alert>
      )}

      {followUpLeads.filter(lead => isFollowupToday(lead.follow_up_date)).length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            You have <strong>{followUpLeads.filter(lead => isFollowupToday(lead.follow_up_date)).length}</strong> follow-ups 
            scheduled for today. Don't forget to call them!
          </Typography>
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label={`My Leads (${myLeads.length})`} />
        <Tab label={`Follow-ups (${followUpLeads.length})`} />
      </Tabs>

      {tabValue === 0 && renderMyLeads()}
      {tabValue === 1 && renderFollowUps()}
    </Box>
  );
};

export default BDADashboard;